/**
 * Theme Context - Provides CMS-controlled theme settings to the entire app
 * Fetches design settings from the Theme API and applies them as CSS variables
 */
import React, { createContext, useContext, useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Default theme values (fallback)
const defaultTheme = {
  // Brand Identity
  brand_name: 'SAMA',
  tagline: 'The Calm Mind Studio',
  logo_light_url: 'https://ehvwjbglvapazrvyzevw.supabase.co/storage/v1/object/public/cms-assets/logos/SAMA_Logo_Primary.png',
  logo_height_header: 80,
  logo_height_footer: 60,
  logo_height_mobile: 60,
  
  // Colors
  colors: {
    primary: '#2F5D50',
    primary_light: '#3d7a68',
    primary_dark: '#1e3d34',
    secondary: '#8B7355',
    accent: '#E8B86D',
    bg_primary: '#ffffff',
    bg_secondary: '#fafaf9',
    bg_tertiary: '#f5f5f4',
    bg_elevated: '#ffffff',
    text_primary: '#1c1917',
    text_secondary: '#78716c',
    text_muted: '#a8a29e',
    success: '#22c55e',
    warning: '#f59e0b',
    error: '#ef4444',
    info: '#3b82f6',
  },
  
  // Typography
  typography: {
    font_heading: 'Playfair Display',
    font_body: 'Inter',
    font_ui: 'Inter',
    heading_weight: 600,
    body_weight: 400,
  },
  
  // Components
  components: {
    border_radius: 24,
    border_radius_sm: 12,
    border_radius_lg: 32,
    shadow_intensity: 'md',
    button_style: 'pill',
    card_padding: 24,
  },
  
  // Layout
  layout: {
    container_width: 1280,
    section_padding: 'comfortable',
  },
  
  // Theme Mode
  theme_mode: 'light',
};

const ThemeContext = createContext({
  theme: defaultTheme,
  isLoading: true,
});

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(defaultTheme);
  const [isLoading, setIsLoading] = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchThemeSettings();
  }, []);

  const fetchThemeSettings = async () => {
    try {
      // Check for preview mode (from Admin Theme Settings)
      const urlParams = new URLSearchParams(window.location.search);
      const isPreviewMode = urlParams.get('preview') === 'theme';
      
      if (isPreviewMode) {
        const previewData = localStorage.getItem('sama_theme_preview');
        if (previewData) {
          try {
            const parsed = JSON.parse(previewData);
            // Check if preview hasn't expired (5 minutes)
            if (parsed.expiresAt && Date.now() < parsed.expiresAt && parsed.config) {
              console.log('Applying preview theme');
              const previewTheme = {
                ...defaultTheme,
                ...parsed.config,
                colors: { ...defaultTheme.colors, ...parsed.config.colors },
                typography: { ...defaultTheme.typography, ...parsed.config.typography },
                components: { ...defaultTheme.components, ...parsed.config.components },
                layout: { ...defaultTheme.layout, ...parsed.config.layout },
              };
              setTheme(previewTheme);
              applyThemeToCSS(previewTheme);
              setIsLoading(false);
              return; // Don't fetch from API in preview mode
            } else {
              // Clear expired preview
              localStorage.removeItem('sama_theme_preview');
            }
          } catch (e) {
            console.error('Failed to parse preview theme:', e);
            localStorage.removeItem('sama_theme_preview');
          }
        }
      }
      
      // Fetch from the theme API
      const response = await fetch(`${API_URL}/api/admin/theme/config/public`);
      if (response.ok) {
        const data = await response.json();
        if (data.config) {
          const newTheme = {
            ...defaultTheme,
            ...data.config,
            colors: { ...defaultTheme.colors, ...data.config.colors },
            typography: { ...defaultTheme.typography, ...data.config.typography },
            components: { ...defaultTheme.components, ...data.config.components },
            layout: { ...defaultTheme.layout, ...data.config.layout },
          };
          setTheme(newTheme);
          applyThemeToCSS(newTheme);
        } else {
          applyThemeToCSS(defaultTheme);
        }
      } else {
        applyThemeToCSS(defaultTheme);
      }
    } catch (error) {
      console.error('Failed to fetch theme settings:', error);
      applyThemeToCSS(defaultTheme);
    } finally {
      setIsLoading(false);
    }
  };

  const applyThemeToCSS = (themeSettings) => {
    const root = document.documentElement;
    const colors = themeSettings.colors || {};
    const typography = themeSettings.typography || {};
    const components = themeSettings.components || {};
    
    // Apply brand colors as CSS variables
    root.style.setProperty('--theme-primary', colors.primary || '#2F5D50');
    root.style.setProperty('--theme-primary-light', colors.primary_light || '#3d7a68');
    root.style.setProperty('--theme-primary-dark', colors.primary_dark || '#1e3d34');
    root.style.setProperty('--theme-secondary', colors.secondary || '#8B7355');
    root.style.setProperty('--theme-accent', colors.accent || '#E8B86D');
    
    // Background colors
    root.style.setProperty('--theme-bg-primary', colors.bg_primary || '#ffffff');
    root.style.setProperty('--theme-bg-secondary', colors.bg_secondary || '#fafaf9');
    root.style.setProperty('--theme-bg-tertiary', colors.bg_tertiary || '#f5f5f4');
    root.style.setProperty('--theme-bg-elevated', colors.bg_elevated || '#ffffff');
    
    // Text colors
    root.style.setProperty('--theme-text', colors.text_primary || '#1c1917');
    root.style.setProperty('--theme-text-muted', colors.text_secondary || '#78716c');
    root.style.setProperty('--theme-text-subtle', colors.text_muted || '#a8a29e');
    
    // Semantic colors
    root.style.setProperty('--theme-success', colors.success || '#22c55e');
    root.style.setProperty('--theme-warning', colors.warning || '#f59e0b');
    root.style.setProperty('--theme-error', colors.error || '#ef4444');
    root.style.setProperty('--theme-info', colors.info || '#3b82f6');
    
    // Card styles
    root.style.setProperty('--theme-card-radius', `${components.border_radius || 24}px`);
    root.style.setProperty('--theme-card-radius-sm', `${components.border_radius_sm || 12}px`);
    root.style.setProperty('--theme-card-radius-lg', `${components.border_radius_lg || 32}px`);
    root.style.setProperty('--theme-card-padding', `${components.card_padding || 24}px`);
    
    // Button styles
    const btnStyle = components.button_style || 'pill';
    const btnRadius = btnStyle === 'pill' ? '9999px' : btnStyle === 'rounded' ? '12px' : '4px';
    root.style.setProperty('--theme-btn-radius', btnRadius);
    root.style.setProperty('--theme-btn-primary-bg', colors.primary || '#2F5D50');
    root.style.setProperty('--theme-btn-primary-text', '#ffffff');
    
    // Shadow based on intensity
    const shadowMap = {
      none: 'none',
      sm: '0 1px 2px rgba(0,0,0,0.05)',
      md: '0 4px 6px rgba(0,0,0,0.07)',
      lg: '0 10px 15px rgba(0,0,0,0.1)',
      xl: '0 20px 25px rgba(0,0,0,0.15)',
    };
    root.style.setProperty('--theme-shadow', shadowMap[components.shadow_intensity] || shadowMap.md);
    
    // Typography
    root.style.setProperty('--theme-font-heading', typography.font_heading || 'Playfair Display');
    root.style.setProperty('--theme-font-body', typography.font_body || 'Inter');
    root.style.setProperty('--theme-font-ui', typography.font_ui || 'Inter');
    root.style.setProperty('--theme-heading-weight', typography.heading_weight || 600);
    root.style.setProperty('--theme-body-weight', typography.body_weight || 400);
    
    // Logo heights
    root.style.setProperty('--theme-logo-header-height', `${themeSettings.logo_height_header || 80}px`);
    root.style.setProperty('--theme-logo-footer-height', `${themeSettings.logo_height_footer || 60}px`);
    root.style.setProperty('--theme-logo-mobile-height', `${themeSettings.logo_height_mobile || 60}px`);
  };

  return (
    <ThemeContext.Provider value={{ theme, isLoading }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
